// Área 4 (#1822) — "heatmap corrigido": override de campos-padrão do ECharts
// sobre o `option` que o back devolve pronto. NÃO toca em option não-heatmap.

export const HEATMAP_INITIAL_WINDOW_COLUMNS = 15;
export const HEATMAP_ROW_HEIGHT_PX = 26;
export const HEATMAP_BASE_HEIGHT_PX = 200;
export const HEATMAP_MIN_HEIGHT_PX = 420;
export const HEATMAP_MAX_HEIGHT_PX = 900;
export const HEATMAP_Y_ZOOM_ROW_THRESHOLD = 20;

type AnyOption = Record<string, unknown>;

function asRecord(value: unknown): AnyOption | undefined {
  return value && typeof value === 'object' ? (value as AnyOption) : undefined;
}

export function isHeatmapOption(option: object | undefined): boolean {
  const opt = asRecord(option);
  if (!opt) return false;
  const { series } = opt;
  if (Array.isArray(series)) return series.some((s) => asRecord(s)?.type === 'heatmap');
  return asRecord(series)?.type === 'heatmap';
}

function axisData(axis: unknown): unknown[] {
  const single = Array.isArray(axis) ? asRecord(axis[0]) : asRecord(axis);
  const data = single?.data;
  return Array.isArray(data) ? data : [];
}

export function rowCount(option: object | undefined): number {
  const opt = asRecord(option);
  return opt ? axisData(opt.yAxis).length : 0;
}

export function heatmapHeight(rows: number): number {
  const raw = HEATMAP_BASE_HEIGHT_PX + rows * HEATMAP_ROW_HEIGHT_PX;
  return Math.min(Math.max(raw, HEATMAP_MIN_HEIGHT_PX), HEATMAP_MAX_HEIGHT_PX);
}

// Extração defensiva: heatmap ECharts usa value = [x, y, v]; fallback p/ escalar.
// Célula vazia (null/undefined/'') vira rótulo vazio — NÃO '0.0' (Number(null)===0).
function roundCellLabel(params: { value?: unknown }): string {
  const { value } = params;
  const raw = Array.isArray(value) ? value[value.length - 1] : value;
  if (raw === null || raw === undefined || raw === '') return '';
  const num = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(num) ? num.toFixed(1) : '';
}

function enhanceSeries(series: unknown): unknown {
  const s = asRecord(series);
  if (!s || s.type !== 'heatmap') return series;
  return {
    ...s,
    label: {
      ...(asRecord(s.label) ?? {}),
      show: true,
      fontSize: 12,
      formatter: roundCellLabel,
    },
  };
}

export default function enhanceHeatmapOption(
  option: object | undefined,
): object | undefined {
  const opt = asRecord(option);
  if (!opt || !isHeatmapOption(opt)) return option;

  const columns = axisData(opt.xAxis).length;
  const xEnd = columns > HEATMAP_INITIAL_WINDOW_COLUMNS
    ? (HEATMAP_INITIAL_WINDOW_COLUMNS / columns) * 100
    : 100;

  const dataZoom: AnyOption[] = [
    {
      type: 'slider', xAxisIndex: 0, filterMode: 'none', start: 0, end: xEnd, bottom: 8,
    },
    {
      type: 'inside', xAxisIndex: 0, filterMode: 'none', start: 0, end: xEnd,
    },
  ];

  const rows = axisData(opt.yAxis).length;
  if (rows > HEATMAP_Y_ZOOM_ROW_THRESHOLD) {
    const yWindow = (HEATMAP_Y_ZOOM_ROW_THRESHOLD / rows) * 100;
    dataZoom.push({
      type: 'slider', yAxisIndex: 0, filterMode: 'none', start: 100 - yWindow, end: 100, right: 8,
    });
  }

  const { series, xAxis: rawXAxis } = opt;
  const enhancedSeries = Array.isArray(series) ? series.map(enhanceSeries) : enhanceSeries(series);

  const xAxis = asRecord(rawXAxis);
  const enhancedXAxis = Array.isArray(rawXAxis)
    ? opt.xAxis
    : { ...(xAxis ?? {}), axisLabel: { ...(asRecord(xAxis?.axisLabel) ?? {}), interval: 0 } };

  return {
    ...opt,
    dataZoom,
    series: enhancedSeries,
    xAxis: enhancedXAxis,
  };
}
